import React, { useState, useEffect } from 'react';
import { ref, get, set, remove, push } from 'firebase/database';
import { database } from '../firebase';

const TemporaryAdminPanel = () => {
    const [temporaryAdmins, setTemporaryAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newAdminEmail, setNewAdminEmail] = useState('');
    const [addingAdmin, setAddingAdmin] = useState(false);

    useEffect(() => {
        loadTemporaryAdmins();
    }, []);

    const loadTemporaryAdmins = async () => {
        try {
            const adminRef = ref(database, 'admins');
            const snapshot = await get(adminRef);

            if (snapshot.exists()) {
                const admins = snapshot.val();
                const tempAdmins = [];

                for (const [userId, isAdmin] of Object.entries(admins)) {
                    if (isAdmin === true) {
                        // Obtener información del usuario
                        const userRef = ref(database, `users/${userId}`);
                        const userSnapshot = await get(userRef);

                        if (userSnapshot.exists()) {
                            const userData = userSnapshot.val();
                            tempAdmins.push({
                                userId,
                                email: userData.email || 'Email no disponible',
                                addedAt: userData.adminAddedAt || Date.now(),
                                isPermanent: userData.isPermanentAdmin || false
                            });
                        }
                    }
                }

                setTemporaryAdmins(tempAdmins.sort((a, b) => b.addedAt - a.addedAt));
            }
        } catch (error) {
            console.error('Error cargando administradores temporales:', error);
        } finally {
            setLoading(false);
        }
    };

    const addTemporaryAdmin = async () => {
        if (!newAdminEmail.trim()) {
            alert('Por favor ingresa un email válido');
            return;
        }

        setAddingAdmin(true);
        try {
            // Buscar usuario por email
            const usersRef = ref(database, 'users');
            const usersSnapshot = await get(usersRef);

            if (!usersSnapshot.exists()) {
                alert('No se encontraron usuarios registrados');
                return;
            }

            const users = usersSnapshot.val();
            let targetUserId = null;

            for (const [userId, userData] of Object.entries(users)) {
                if (userData.email === newAdminEmail.trim()) {
                    targetUserId = userId;
                    break;
                }
            }

            if (!targetUserId) {
                alert('Usuario no encontrado con ese email');
                return;
            }

            // Agregar como administrador temporal
            const adminRef = ref(database, `admins/${targetUserId}`);
            await set(adminRef, true);

            // Actualizar información del usuario
            const userRef = ref(database, `users/${targetUserId}`);
            await set(userRef, {
                ...users[targetUserId],
                adminAddedAt: Date.now(),
                isPermanentAdmin: false
            });

            setNewAdminEmail('');
            loadTemporaryAdmins();
            alert('Administrador temporal agregado exitosamente');
        } catch (error) {
            console.error('Error agregando administrador temporal:', error);
            alert('Error al agregar administrador temporal');
        } finally {
            setAddingAdmin(false);
        }
    };

    const removeTemporaryAdmin = async (userId) => {
        if (!confirm('¿Estás seguro de que quieres remover los permisos de administrador de este usuario?')) {
            return;
        }

        try {
            const adminRef = ref(database, `admins/${userId}`);
            await remove(adminRef);

            // Actualizar información del usuario
            const userRef = ref(database, `users/${userId}`);
            const userSnapshot = await get(userRef);

            if (userSnapshot.exists()) {
                const userData = userSnapshot.val();
                await set(userRef, {
                    ...userData,
                    adminAddedAt: null,
                    isPermanentAdmin: false
                });
            }

            loadTemporaryAdmins();
            alert('Administrador temporal removido exitosamente');
        } catch (error) {
            console.error('Error removiendo administrador temporal:', error);
            alert('Error al remover administrador temporal');
        }
    };

    const formatDate = (timestamp) => {
        return new Date(timestamp).toLocaleString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return <div className="loading">Cargando administradores...</div>;
    }

    return (
        <div className="temporary-admin-panel">
            <div className="panel-section">
                <h3>Agregar Administrador Temporal</h3>
                <div className="add-admin-form">
                    <div className="form-group">
                        <label>Email del usuario:</label>
                        <input
                            type="email"
                            value={newAdminEmail}
                            onChange={(e) => setNewAdminEmail(e.target.value)}
                            placeholder="usuario@email.com"
                            disabled={addingAdmin}
                        />
                    </div>
                    <button
                        onClick={addTemporaryAdmin}
                        disabled={addingAdmin || !newAdminEmail.trim()}
                        className="add-admin-btn"
                    >
                        {addingAdmin ? 'Agregando...' : '👑 Agregar Administrador'}
                    </button>
                </div>
            </div>

            <div className="panel-section">
                <h3>Administradores Temporales</h3>
                <div className="admins-list">
                    {temporaryAdmins.length === 0 ? (
                        <p className="no-admins">No hay administradores temporales</p>
                    ) : (
                        temporaryAdmins.map(admin => (
                            <div key={admin.userId} className="admin-item">
                                <div className="admin-info">
                                    <div className="admin-avatar">
                                        👑
                                    </div>
                                    <div className="admin-details">
                                        <h4>{admin.email}</h4>
                                        <div className="admin-meta">
                                            <span className="admin-type">
                                                {admin.isPermanent ? 'Permanente' : 'Temporal'}
                                            </span>
                                            <span className="admin-date">
                                                Agregado: {formatDate(admin.addedAt)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="admin-actions">
                                    {!admin.isPermanent && (
                                        <button
                                            onClick={() => removeTemporaryAdmin(admin.userId)}
                                            className="remove-admin-btn"
                                            title="Remover permisos de administrador"
                                        >
                                            ❌ Remover
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            <div className="panel-section">
                <h3>Información Importante</h3>
                <div className="info-box">
                    <div className="info-item">
                        <h4>🔐 Permisos de Administrador</h4>
                        <p>Los administradores temporales tienen acceso completo al panel de administración, incluyendo gestión de licencias, notificaciones y configuración del sistema.</p>
                    </div>

                    <div className="info-item">
                        <h4>⚠️ Responsabilidades</h4>
                        <p>Los administradores pueden enviar notificaciones a todos los usuarios, gestionar licencias y modificar configuraciones del sistema. Usa esta función con precaución.</p>
                    </div>

                    <div className="info-item">
                        <h4>📊 Auditoría</h4>
                        <p>Todas las acciones de administradores se registran automáticamente para mantener un historial de cambios y garantizar la seguridad del sistema.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TemporaryAdminPanel;